import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * StructuredDataView
 *
 * Renders an arbitrary JSON-shaped value (event payload/result, a runtime
 * trace, an audit diff) as a friendly label/value tree instead of a raw
 * JSON.stringify dump — so an operator can scan a record without parsing
 * braces and quoting.
 * Falls back to nothing renderable only for an empty object/array; primitives
 * and every nesting depth still render, just without special formatting.
 *
 * Shared across apps (moved out of apps/admin — see GitHub issue #961) — it
 * has no admin-specific coupling, so keep it that way: no app-specific
 * routing, data fetching, or styling assumptions beyond the dark-surface
 * "structured data box" chrome its consumers already wrap it in.
 */
import { colors } from '../tokens';
const s = {
    dl: { margin: 0, display: 'flex', flexDirection: 'column', gap: '0.375rem' },
    row: { display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap' },
    dt: { fontSize: '0.75rem', color: colors.gray[500], minWidth: '9rem', flexShrink: 0 },
    dd: { margin: 0, fontSize: '0.8125rem', color: colors.gray[200], wordBreak: 'break-word' },
    nullValue: { color: colors.gray[600], fontStyle: 'italic' },
    list: { margin: 0, paddingLeft: '1.125rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' },
    nested: { marginTop: '0.25rem', paddingLeft: '0.75rem', borderLeft: `2px solid ${colors.gray[700]}` },
};
function humanizeKey(key) {
    const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function renderValue(value) {
    if (value === null || value === undefined)
        return _jsx("span", { style: s.nullValue, children: "\u2014" });
    if (typeof value === 'boolean')
        return _jsx("span", { children: value ? 'Yes' : 'No' });
    if (typeof value === 'number')
        return _jsx("span", { children: value.toLocaleString() });
    if (typeof value === 'string')
        return _jsx("span", { children: value || _jsx("span", { style: s.nullValue, children: "(empty)" }) });
    if (Array.isArray(value)) {
        if (value.length === 0)
            return _jsx("span", { style: s.nullValue, children: "(empty list)" });
        return (_jsx("ul", { style: s.list, children: value.map((item, i) => (_jsx("li", { children: isPlainObject(item) ? renderObject(item) : renderValue(item) }, i))) }));
    }
    if (isPlainObject(value))
        return renderObject(value);
    return _jsx("span", { children: String(value) });
}
function renderObject(obj) {
    const keys = Object.keys(obj);
    if (keys.length === 0)
        return _jsx("span", { style: s.nullValue, children: "(empty)" });
    return (_jsx("dl", { style: s.dl, children: keys.map((key) => {
            const value = obj[key];
            const nested = isPlainObject(value) || Array.isArray(value);
            return (_jsxs("div", { style: s.row, children: [_jsx("dt", { style: s.dt, children: humanizeKey(key) }), _jsx("dd", { style: s.dd, children: nested ? _jsx("div", { style: s.nested, children: renderValue(value) }) : renderValue(value) })] }, key));
        }) }));
}
export function StructuredDataView({ data }) {
    if (data === null || data === undefined)
        return null;
    if (isPlainObject(data) && Object.keys(data).length === 0)
        return null;
    if (Array.isArray(data) && data.length === 0)
        return null;
    return _jsx(_Fragment, { children: renderValue(data) });
}
export default StructuredDataView;
//# sourceMappingURL=StructuredDataView.js.map